import { ReactNode, createContext, useContext } from 'react'
import { ButtonAppearance, ButtonColor, ButtonSize } from '@/components/Button'

interface ToggleButtonGroupContextValue {
	value: string | string[]
	onValueChange: (value: string) => void
	type: 'single' | 'multiple'
	activeAppearance: ButtonAppearance
	activeColor: ButtonColor
	inactiveAppearance: ButtonAppearance
	inactiveColor: ButtonColor
	size: ButtonSize
}

const ToggleButtonGroupContext =
	createContext<ToggleButtonGroupContextValue | null>(null)

export function useToggleButtonGroup () {
	return useContext(ToggleButtonGroupContext)
}

interface ToggleButtonGroupProps {
	type: 'single' | 'multiple'
	value?: string | string[]
	defaultValue?: string | string[]
	onValueChange?: (value: string | string[]) => void
	appearance?: 'solid' | 'outline'
	size?: ButtonSize
	orientation?: 'horizontal' | 'vertical'
	activeAppearance?: ButtonAppearance
	activeColor?: ButtonColor
	inactiveAppearance?: ButtonAppearance
	inactiveColor?: ButtonColor
	disabled?: boolean
	children: ReactNode
	spacesClass?: string
	className?: string
}

export function ToggleButtonGroup ({
	type,
	value,
	onValueChange,
	appearance = 'solid',
	size = 'md',
	orientation = 'horizontal',
	activeAppearance = 'solid',
	activeColor = 'brand',
	inactiveAppearance = 'ghost',
	inactiveColor = 'content3',
	children,
	spacesClass = '',
	className = ''
}: ToggleButtonGroupProps) {
	const handleChange = (itemValue: string) => {
		if (type === 'single') {
			onValueChange?.(itemValue)
		} else {
			const current = Array.isArray(value) ? value : []
			const next = current.includes(itemValue)
				? current.filter(v => v !== itemValue)
				: [...current, itemValue]
			onValueChange?.(next)
		}
	}

	const bgClass =
		appearance === 'solid'
			? 'spaces:bg-canvas-4 spaces:border-transparent'
			: 'spaces:bg-canvas-base spaces:border-canvas-3'

	const radiusClass =
		size === 'xs'
			? 'spaces:rounded-8'
			: size === 'sm'
			? 'spaces:rounded-12'
			: size === 'md'
			? 'spaces:rounded-16'
			: 'spaces:rounded-20'
	const dirClass = orientation === 'vertical' ? 'spaces:flex-col' : 'spaces:flex-row'

	return (
		<ToggleButtonGroupContext.Provider
			value={{
				value: value ?? '',
				onValueChange: handleChange,
				type,
				activeAppearance,
				activeColor,
				inactiveAppearance,
				inactiveColor,
				size
			}}
		>
			<div
				role='group'
				className={`spaces-toggle-group spaces:inline-flex ${dirClass} ${bgClass} ${radiusClass} spaces:border spaces:p-4 spaces:gap-0 ${spacesClass} ${className}`}
			>
				{children}
			</div>
		</ToggleButtonGroupContext.Provider>
	)
}
